/**
 * LogDashboard — Iter 209 Part 2.
 * Reads /workspaces/{ws}/log/dashboard and renders KPI cards +
 * distribution charts + open-high-severity, overdue, stale, recent lists.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { AlertTriangle, Flame, Hourglass, ListChecks, Layers, ArrowLeft } from "lucide-react";
import { LOG_KINDS, KindChip } from "@/components/LogModule";

const SEV_COLOR = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-500",
  low: "bg-slate-500",
};

export default function LogDashboard() {
  const { activeWorkspaceId } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    api.get(`/workspaces/${activeWorkspaceId}/log/dashboard`)
      .then((r) => setD(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load dashboard")))
      .finally(() => setBusy(false));
  }, [activeWorkspaceId]);

  const byKind = d?.by_kind || {};
  const byStatus = d?.by_status || {};
  const bySev = d?.by_severity || {};
  const maxKind = Math.max(1, ...Object.values(byKind));
  const total = d?.total ?? 0;

  return (
    <>
      <div className="px-4 sm:px-6 md:px-10 pt-6">
        <Link to="/app/log" className="text-sm label-eyebrow inline-flex items-center hover:text-primary" data-testid="log-dash-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back to Log
        </Link>
      </div>
      <PageHeader
        eyebrow="Workspace"
        title="Log dashboard"
        description="Health of every risk, action, issue, decision, opportunity and lesson across your portfolio."
        testid="log-dash-header"
      />
      <div className="px-4 sm:px-6 md:px-10 pb-12 space-y-6" data-testid="log-dash">
        {busy && <div className="text-sm text-meta">Loading…</div>}
        {!busy && d && (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="log-dash-kpi">
              <KpiCard icon={Layers} label="Total items" value={total} testid="log-kpi-total" />
              <KpiCard icon={Flame} label="High-severity · open" value={(d.open_high_severity || []).length} accent="text-red-500" testid="log-kpi-highsev" />
              <KpiCard icon={AlertTriangle} label="Overdue" value={(d.overdue || []).length} accent="text-orange-500" testid="log-kpi-overdue" />
              <KpiCard icon={Hourglass} label="Stale (≥14d open)" value={(d.stale || []).length} accent="text-amber-600" testid="log-kpi-stale" />
              <KpiCard icon={ListChecks} label="Recent (last 20)" value={(d.recent || []).length} testid="log-kpi-recent" />
            </div>

            {/* Distribution: by kind */}
            <section className="border border-border p-4">
              <div className="label-eyebrow mb-3">By kind</div>
              <div className="space-y-1.5" data-testid="log-by-kind">
                {LOG_KINDS.map((k) => {
                  const v = byKind[k.v] || 0;
                  const pct = Math.round((v / maxKind) * 100);
                  return (
                    <div key={k.v} className="flex items-center gap-3">
                      <div className="w-32 shrink-0"><KindChip kind={k.v} /></div>
                      <div className="flex-1 bg-muted h-4 relative overflow-hidden">
                        <div className={`absolute inset-y-0 left-0 ${k.cls}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-10 text-right text-xs font-semibold">{v}</div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Two-col: status + severity */}
            <div className="grid md:grid-cols-2 gap-4">
              <section className="border border-border p-4" data-testid="log-by-status">
                <div className="label-eyebrow mb-3">By status</div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(byStatus).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                    <div key={s} className="border border-border p-3">
                      <div className="text-[10px] label-eyebrow">{s.replace("_", " ")}</div>
                      <div className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{n}</div>
                    </div>
                  ))}
                  {Object.keys(byStatus).length === 0 && <div className="text-xs text-meta col-span-2">No data yet.</div>}
                </div>
              </section>
              <section className="border border-border p-4" data-testid="log-by-severity">
                <div className="label-eyebrow mb-3">By severity</div>
                <div className="space-y-2">
                  {["critical", "high", "medium", "low"].map((sv) => {
                    const v = bySev[sv] || 0;
                    return (
                      <div key={sv} className="flex items-center gap-3">
                        <div className="w-16 text-xs capitalize">{sv}</div>
                        <div className="flex-1 bg-muted h-4 relative overflow-hidden">
                          <div className={`absolute inset-y-0 left-0 ${SEV_COLOR[sv]}`}
                               style={{ width: `${Math.min(100, v * 100 / Math.max(1, total))}%` }} />
                        </div>
                        <div className="w-10 text-right text-xs font-semibold">{v}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            {/* Intelligence lists */}
            <div className="grid md:grid-cols-2 gap-4">
              <IntelList title="🔴 High-severity · open" rows={d.open_high_severity || []} testid="log-intel-highsev" />
              <IntelList title="⏰ Overdue" rows={d.overdue || []} testid="log-intel-overdue" />
              <IntelList title="⌛ Stale (≥14d open)" rows={d.stale || []} testid="log-intel-stale" />
              <IntelList title="🆕 Recent (last 20)" rows={d.recent || []} testid="log-intel-recent" showAge />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function KpiCard({ icon: Icon, label, value, accent = "text-foreground", testid }) {
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 label-eyebrow text-[10px]">
        <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
      </div>
      <div className={`text-3xl font-bold mt-1 ${accent}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function IntelList({ title, rows, testid, showAge }) {
  return (
    <section className="border border-border p-4" data-testid={testid}>
      <div className="label-eyebrow mb-2">{title} · {rows.length}</div>
      {rows.length === 0 && <div className="text-xs text-meta">Nothing here — good.</div>}
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {rows.map((r) => (
          <Link key={r.id}
                to={`/app/log?open=${r.id}`}
                className="block border border-border p-2 hover:border-primary/40 hover:bg-muted/40"
                data-testid={`${testid}-row-${r.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="font-semibold text-xs truncate flex-1">{r.title}</div>
              <KindChip kind={r.kind} />
            </div>
            <div className="flex items-center gap-2 text-[10px] text-meta">
              <span className="chip text-[9px]">{(r.status || "open").replace("_", " ")}</span>
              {r.severity && <span className="chip text-[9px]">{r.severity}</span>}
              {r.due_date && <span>due {String(r.due_date).slice(0, 10)}</span>}
              {r.brand_name && <span>· {r.brand_name}</span>}
              {r.client_name && <span>· {r.client_name}</span>}
              {showAge && <span className="ml-auto">{fmtRelative(r.created_at)}</span>}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
